import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './ReportsDashboard.css';
import Navbar from '../../shared/navbar';
import AIReportSearchBar from './AIReportSearchBar';


const ReportsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    monthlyRevenue: 0,
    occupancyRate: 0,
    newApplications: 0,
    leaseRenewals: 0,
    revenueChange: 0,
    occupancyChange: 0,
    properties: [],
    monthlyTrends: [],
    topTenants: []
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedProperty, setSelectedProperty] = useState(null);

  useEffect(() => {
    loadReportData();
  }, [dateRange.startDate, dateRange.endDate, selectedProperty]);

  const loadReportData = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('start_date', dateRange.startDate);
      if (dateRange.endDate) params.append('end_date', dateRange.endDate);
      if (selectedProperty) params.append('property_id', selectedProperty);
      
      // Fetch comprehensive analytics from backend API
      const url = `${API_BASE_URL}/api/reports/analytics${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }
      
      const data = await response.json();
      
      setReportData({
        monthlyRevenue: data.monthlyRevenue || 0,
        occupancyRate: data.occupancyRate || 0,
        newApplications: data.newApplications || 0,
        leaseRenewals: data.leaseRenewals || 0,
        revenueChange: data.revenueChange || 0,
        occupancyChange: data.occupancyChange || 0,
        properties: data.properties || [],
        monthlyTrends: data.monthlyTrends || [],
        topTenants: data.topTenants || []
      });
    } catch (error) {
      console.error('Error loading report data:', error);
      // Set default/empty data on error
      setReportData({
        monthlyRevenue: 0,
        occupancyRate: 0,
        newApplications: 0,
        leaseRenewals: 0,
        revenueChange: 0,
        occupancyChange: 0,
        properties: [],
        monthlyTrends: [],
        topTenants: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleFilter = () => setShowFilterModal(true);



  const handleExportReport = async () => {
    try {
      // Generate export data
      const exportData = {
        reportDate: new Date().toISOString(),
        monthlyRevenue: reportData.monthlyRevenue,
        occupancyRate: reportData.occupancyRate,
        newApplications: reportData.newApplications,
        leaseRenewals: reportData.leaseRenewals,
        properties: reportData.properties,
        monthlyTrends: reportData.monthlyTrends,
        topTenants: reportData.topTenants
      };

      // Convert to CSV format
      const csvRows = [];
      csvRows.push(['Reports & Analytics Export']);
      csvRows.push(['Generated:', new Date().toLocaleString()]);
      csvRows.push([]);
      csvRows.push(['KPIs']);
      csvRows.push(['Monthly Revenue', formatCurrency(reportData.monthlyRevenue)]);
      csvRows.push(['Occupancy Rate', `${reportData.occupancyRate}%`]);
      csvRows.push(['New Applications', reportData.newApplications]);
      csvRows.push(['Lease Renewals', reportData.leaseRenewals]);
      csvRows.push([]);
      csvRows.push(['Property Performance']);
      csvRows.push(['Property', 'Units', 'Occupied', 'Occupancy %', 'Revenue', 'Avg Rent']);
      reportData.properties.forEach(prop => {
        csvRows.push([
          prop.name,
          prop.units,
          prop.occupied,
          `${prop.occupancy}%`,
          formatCurrency(prop.revenue),
          formatCurrency(prop.avgRent)
        ]);
      });

      const csvContent = csvRows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `reports-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const handleApplyFilters = async () => {
    setShowFilterModal(false);
    // Reload data with filters
    await loadReportData();
  };

  const handleClearFilters = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedProperty(null);
    setShowFilterModal(false);
    loadReportData();
  };

  if (loading) {
    return (
      <div className="reports-dashboard-container">
        <Navbar selected="Reporting" />
        <div className="reports-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-dashboard-container">
      <Navbar selected="Reporting" />
      <div className="reports-content">
        {/* Header */}
        <div className="reports-header">
          <div className="reports-header-left">
            <h1 className="reports-title">Reports & Analytics</h1>
            <p className="reports-subtitle">Track performance and generate insights</p>
          </div>
          <div className="reports-header-right">
            <button className="reports-filter-btn" onClick={handleFilter}>
              <span className="reports-btn-icon">🔽</span>
              Filter
            </button>
            <button className="reports-export-btn" onClick={handleExportReport}>
              <span className="reports-btn-icon">⬇️</span>
              Export Report
            </button>
          </div>
        </div>

        {/* AI Search Bar */}
        <div className="reports-ai-search">
          <AIReportSearchBar />
        </div>

        {/* Filter Modal */}
        {showFilterModal && (
          <div className="filter-modal-overlay" onClick={() => setShowFilterModal(false)}>
            <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
              <div className="filter-modal-header">
                <h3>Filter Reports</h3>
                <button onClick={() => setShowFilterModal(false)} className="reports-close-btn">×</button>
              </div>
              <div className="filter-modal-body">
                <div className="filter-group">
                  <label>Date Range</label>
                  <div className="date-range-inputs">
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                      className="date-input"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                      className="date-input"
                    />
                  </div>
                </div>
                <div className="filter-actions">
                  <button onClick={handleClearFilters} className="filter-button-clear">
                    Clear Filters
                  </button>
                  <button onClick={handleApplyFilters} className="filter-button-apply">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="kpi-cards">
          <div className="kpi-card">
            <div className="kpi-icon revenue-icon">$</div>
            <div className="kpi-content">
              <h3 className="kpi-label">Monthly Revenue</h3>
              <div className="kpi-value">{formatCurrency(reportData.monthlyRevenue)}</div>
              <div className={`kpi-change ${reportData.revenueChange < 0 ? 'negative' : 'positive'}`}>
                <span className="change-icon">{reportData.revenueChange < 0 ? '↓' : '↑'}</span>
                {Math.abs(reportData.revenueChange)}% from last month
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon occupancy-icon">🏢</div>
            <div className="kpi-content">
              <h3 className="kpi-label">Occupancy Rate</h3>
              <div className="kpi-value">{reportData.occupancyRate}%</div>
              <div className={`kpi-change ${reportData.occupancyChange < 0 ? 'negative' : 'positive'}`}>
                <span className="change-icon">{reportData.occupancyChange < 0 ? '↓' : '↑'}</span>
                {Math.abs(reportData.occupancyChange)}% from last month
              </div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon applications-icon">👥</div>
            <div className="kpi-content">
              <h3 className="kpi-label">New Applications</h3>
              <div className="kpi-value">{reportData.newApplications}</div>
              <div className="kpi-meta">This month</div>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon renewals-icon">📅</div>
            <div className="kpi-content">
              <h3 className="kpi-label">Lease Renewals</h3>
              <div className="kpi-value">{reportData.leaseRenewals}</div>
              <div className="kpi-meta">This month</div>
            </div>
          </div>
        </div>

        {/* Property Performance Table */}
        <div className="property-performance-section">
          <h2 className="reports-section-title">Property Performance</h2>
          <div className="performance-table-container">
            <table className="performance-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Units</th>
                  <th>Occupancy</th>
                  <th>Revenue</th>
                  <th>Avg Rent</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {reportData.properties.map((property, index) => (
                  <tr key={index}>
                    <td className="property-name-cell">{property.name}</td>
                    <td>{property.units}</td>
                    <td>{property.occupied}/{property.units}</td>
                    <td className="revenue-cell">{formatCurrency(property.revenue)}</td>
                    <td>{formatCurrency(property.avgRent)}</td>
                    <td>
                      <span className={`rate-badge ${property.occupancy >= 95 ? 'high' : property.occupancy >= 90 ? 'medium' : 'low'}`}>
                        {property.occupancy}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Sections */}
        <div className="bottom-sections">
          {/* Monthly Trends */}
          <div className="trends-section">
            <h2 className="reports-section-title">Monthly Trends</h2>
            <div className="trends-list">
              {reportData.monthlyTrends.map((trend, index) => (
                <div key={index} className="trend-item">
                  <div className="trend-month">{trend.month}</div>
                  <div className="trend-details">
                    <div className="trend-row">
                      <span className="trend-label">Occupancy:</span>
                      <span className="trend-value">{trend.occupancy}%</span>
                    </div>
                    <div className="trend-row">
                      <span className="trend-label">Applications:</span>
                      <span className="trend-value">{trend.applications}</span>
                    </div>
                    <div className="trend-row">
                      <span className="trend-label">Renewals:</span>
                      <span className="trend-value">{trend.renewals}</span>
                    </div>
                    <div className="trend-row">
                      <span className="trend-label">Revenue:</span>
                      <span className="trend-value revenue">{formatCurrency(trend.revenue)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Performing Tenants */}
          <div className="tenants-section">
            <h2 className="reports-section-title">Top Performing Tenants</h2>
            <div className="tenants-list">
              {reportData.topTenants.map((tenant, index) => (
                <div key={index} className="tenant-item">
                  <div className="tenant-name">{tenant.name}</div>
                  <div className="tenant-property">{tenant.propertyUnit}</div>
                  <div className="tenant-payments">
                    On-time payments: {tenant.onTimePayments}/{tenant.totalPayments}
                  </div>
                  <div className="tenant-score-container">
                    <span className={`score-badge ${tenant.score >= 95 ? 'high' : tenant.score >= 85 ? 'medium' : 'low'}`}>
                      {tenant.score}% Score
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboard;

